/**
 * MARKETING COMPASS — route shell for /marketing.
 *
 * Owns the licence gate and the feature flag; the quiz itself is
 * MarketingQuiz.jsx. Locked members see the intro's value proposition and an
 * "included with your plan" CTA (spec §3.5) rather than a dead end.
 *
 * The gate here is UI only. Every callable behind the quiz re-checks
 * entitlement server-side against `licenses` via the Admin SDK, so a member who
 * bypasses this screen still cannot read or write a thing.
 */
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { hasMarketingAccess, ACCESS } from '../hub/hubAccess';
import { loadQuizResponse, startQuiz } from './marketingApi';
import { useFeatureFlags } from '../config/featureFlags';
import MarketingQuiz from './MarketingQuiz';
import MarketingResults from './MarketingResults';
import usePlatformData from './usePlatformData';
import ToolNav from '../toolkit/ToolNav';
import * as T from '../brand/tokens';
import { Compass, Lock, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';

export default function MarketingCompass({ books, audiobooks, subscriberCount, embedded = false }) {
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const [access, setAccess] = useState({ state: ACCESS.LOADING, licence: null });
  // The view is DECIDED FROM SERVER STATE, not defaulted.
  //
  // It used to default to 'quiz' unless ?view=results was in the URL, and the
  // quiz then asked only for an UNFINISHED response — so a member who had
  // completed the quiz came back to /marketing and saw the intro, as if none of
  // it had happened. Their answers and recommendation were in Firestore all
  // along; nothing ever loaded them.
  //
  //   completed   → results
  //   in progress → quiz (resumes at the first unanswered question)
  //   neither     → intro
  const [view, setView] = useState(null);        // null = still deciding
  const [quizState, setQuizState] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (access.state !== ACCESS.GRANTED) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const state = await loadQuizResponse();
        if (cancelled) return;
        setQuizState(state);
        const deepLink = typeof window !== 'undefined'
          && new URLSearchParams(window.location.search).get('view') === 'results';
        setView(deepLink || (state.completed && !state.inProgress) ? 'results' : 'quiz');
      } catch (err) {
        // Entitlement is already settled, so this is a transient failure —
        // fall back to the quiz rather than a dead end.
        if (!cancelled) setView('quiz');
      }
    })();
    return () => { cancelled = true; };
  }, [access.state]);

  // Retake = fresh blank response; Edit = reopen the finished one in place.
  const beginQuiz = async (mode) => {
    setStarting(true);
    try {
      await startQuiz(mode);
      setQuizState(null);
      setView('quiz');
    } finally {
      setStarting(false);
    }
  };

  // On the standalone route there is no AuthorDashboardContext, so load the
  // pre-fill inputs here. When embedded in the dashboard the caller passes them
  // in and these reads are skipped.
  const loaded = usePlatformData();
  const platform = {
    books: books ?? loaded.books,
    audiobooks: audiobooks ?? loaded.audiobooks,
    subscriberCount: subscriberCount ?? loaded.subscriberCount,
  };

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      const result = await hasMarketingAccess(user);
      if (!cancelled) setAccess(result);
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  const loading = flagsLoading || access.state === ACCESS.LOADING || loaded.loading
    || (access.state === ACCESS.GRANTED && view === null);

  const Shell = ({ children }) => (
    embedded
      ? <div>{children}</div>
      : (
        <div className={T.PAGE + " " + T.BODY_FONT}>
          <div className={T.SHELL}>
            {/* World 1 only. The back link used to point at /author/dashboard,
                which bounced signed-out visitors into ARC's login. */}
            <ToolNav current="marketing" variant="ink" className="mb-5" />
            {children}
          </div>
        </div>
      )
  );

  if (loading) {
    return (
      <Shell>
        <div className={"flex items-center gap-2 p-6 " + T.ON_INK}>
          <Loader2 className="h-4 w-4 animate-spin" /> Checking your licence…
        </div>
      </Shell>
    );
  }

  // Flag off — the surface isn't live yet.
  if (!flags.marketingCompass) {
    return (
      <Shell>
        <div className={T.PANEL}>
          <div className={T.PANEL_INNER}>
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-gold-soft bg-gold/10 text-gold-deep">
              <Compass className="h-5 w-5" />
            </div>
            <h1 className={T.H1_ON_PARCHMENT}>Marketing Compass is coming soon</h1>
            <p className={"mt-1 text-[17px] " + T.BODY_MUTED}>
              Answer a few questions and get a marketing tool stack picked for your book, your
              genre and your budget. We'll let you know the moment it's ready.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // Licensed — quiz, then results. `view` lets the results screen send the
  // member back to their answers and the quiz hand off on completion.
  if (access.state === ACCESS.GRANTED) {
    return (
      <Shell>
        {view === 'results' ? (
          <MarketingResults
            busy={starting}
            onEditAnswers={() => beginQuiz('edit')}
            onRetake={() => beginQuiz('fresh')}
          />
        ) : (
          <MarketingQuiz
            key={quizState ? 'resume' : 'restart'}
            books={platform.books}
            audiobooks={platform.audiobooks}
            subscriberCount={platform.subscriberCount}
            onComplete={() => { setQuizState(null); setView('results'); }}
          />
        )}
      </Shell>
    );
  }

  // Locked. Spec §3.5: the intro stays visible, the quiz does not.
  const expired = access.state === ACCESS.EXPIRED;
  return (
    <Shell>
      <div className={T.PANEL}>
        <div className={T.PANEL_INNER}>
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-gold-soft bg-gold/15 text-gold-deep">
            <Compass className="h-5 w-5" />
          </div>
          <h1 className={T.H1_ON_PARCHMENT}>Marketing Compass</h1>
          <p className={"mt-2 text-[17px] leading-relaxed " + T.BODY_MUTED}>
            Answer ~16 questions — fewer if we can fill some in from your books — and get a
            marketing tool stack matched to your genre, your stage and your budget, with an
            honest note on what we'd skip and why.
          </p>

          <div className={"mt-4 flex items-center gap-2 rounded-xl px-4 py-3 " + T.PANEL_MUTED}>
            <Lock className="h-4 w-4 shrink-0 text-gold-deep" />
            <p className={T.BODY_MUTED}>
              {expired
                ? 'Your licence has expired. Renew to pick up where you left off.'
                : access.state === ACCESS.SIGNED_OUT
                  ? 'Sign in to Narratoria to use Marketing Compass — it comes with your plan.'
                  : 'Included with your Narratoria plan — no extra charge.'}
            </p>
          </div>

          {access.state === ACCESS.SIGNED_OUT ? (
            <button
              type="button"
              className={T.BTN_PRIMARY + " mt-4"}
              onClick={() => { window.location.href = '/'; }}
            >
              Sign in <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              className={T.BTN_PRIMARY + " mt-4"}
              onClick={() => { window.location.href = '/publishing-roadmap?view=plans'; }}
            >
              {expired ? 'Renew licence' : 'View plans'} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}

          {access.state === ACCESS.ERROR && (
            <p className={"mt-3 text-sm " + T.BODY_FAINT}>
              We couldn't check your licence just now.{' '}
              <button onClick={() => window.location.reload()} className="font-semibold text-gold-deep underline">
                Retry
              </button>
            </p>
          )}
        </div>
      </div>
    </Shell>
  );
}
